// src/hooks/useCitySearchForBulkOrder.js
import { useState, useCallback, useRef, useMemo } from 'react';

const useCitySearchForBulkOrder = (cities = [], onCitySelect) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showResults, setShowResults] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  const searchTimeoutRef = useRef(null);
  
  // Fuzzy match function
  const fuzzyMatch = useCallback((text, query) => {
    if (!query) return { score: 0, matches: false };
    
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();

    // Exact match (case-insensitive) gets the highest score
    if (textLower === queryLower) {
      return { score: 300, matches: true };
    }

    // Prefix match (text starts with query) gets second highest score
    if (textLower.startsWith(queryLower)) {
      return { score: 200, matches: true };
    }
    
    // Direct substring match (query appears anywhere in text)
    if (textLower.includes(queryLower)) {
      return { score: 100, matches: true };
    }
    
    // Character-by-character fuzzy matching
    let textIndex = 0;
    let queryIndex = 0;
    let matches = 0;
    
    while (textIndex < textLower.length && queryIndex < queryLower.length) {
      if (textLower[textIndex] === queryLower[queryIndex]) {
        matches++;
        queryIndex++;
      }
      textIndex++;
    }
    
    if (queryIndex === queryLower.length) {
      const score = (matches / queryLower.length) * 50;
      return { score, matches: true };
    }
    
    return { score: 0, matches: false };
  }, []);
  
  // Search and sort cities
  const filteredCities = useMemo(() => {
    // Ensure cities is an array
    const citiesArray = Array.isArray(cities) ? cities : [];
    
    if (!searchTerm.trim()) return citiesArray.slice(0, 10);
    
    const results = citiesArray.map(city => {
      const cityName = city.name || '';
      
      const cityMatch = fuzzyMatch(cityName, searchTerm);
      
      const bestScore = Math.max(cityMatch.score);
      const hasMatch = cityMatch.matches;
      
      return {
        ...city,
        score: bestScore,
        hasMatch
      };
    })
    .filter(city => city.hasMatch)
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);
    
    return results;
  }, [cities, searchTerm, fuzzyMatch]);
  
  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setSelectedIndex(-1);
    setShowResults(true);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (!value.trim()) {
        setShowResults(false);
      }
    }, 150);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!showResults || filteredCities.length === 0) return;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCities.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : filteredCities.length - 1);
        break;
        
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredCities.length) {
          selectCity(filteredCities[selectedIndex]);
        }
        break;
        
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
        
      default:
        break;
    }
  }, [showResults, filteredCities, selectedIndex]);
  
  // Handle city selection
  const selectCity = useCallback((city) => {
    setSearchTerm(city.name);
    setShowResults(false);
    setIsOpen(false);
    setSelectedIndex(-1);
    
    if (onCitySelect) {
      onCitySelect(city);
    }
  }, [onCitySelect]);
  
  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setShowResults(false);
    setIsOpen(false);
    setSelectedIndex(-1);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);
  
  // Close dropdown
  const closeDropdown = useCallback(() => {
    setShowResults(false);
    setIsOpen(false);
    setSelectedIndex(-1);
  }, []);
  
  // Open dropdown
  const openDropdown = useCallback(() => {
    setIsOpen(true);
    const citiesArray = Array.isArray(cities) ? cities : [];
    if (searchTerm.trim() || citiesArray.length > 0) {
      setShowResults(true);
    }
  }, [searchTerm, cities]);
  
  // Format city display
  const formatCityDisplay = useCallback((city) => {
    return `${city.name} - ${city.District?.name}, ${city.District?.Province?.name}`;
  }, []);
  
  return {
    // State
    searchTerm,
    filteredCities,
    selectedIndex,
    showResults,
    isOpen,
    
    // Handlers
    handleSearchChange,
    handleKeyDown,
    selectCity,
    clearSearch,
    closeDropdown,
    openDropdown,
    
    // Utilities
    formatCityDisplay
  };
};

export default useCitySearchForBulkOrder;